import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabaseClient';
import LanguageSwitcher from '../components/LanguageSwitcher';

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSessionSet, setIsSessionSet] = useState(false);
  const navigate = useNavigate();

  const { updatePassword } = useAuth();

  useEffect(() => {
    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.substring(1));

    const hashAccessToken = hashParams.get('access_token');
    const hashRefreshToken = hashParams.get('refresh_token');
    const hashType = hashParams.get('type');

    if (hashType !== 'recovery' || !hashAccessToken || !hashRefreshToken) {
      setError('Invalid or expired reset link. Please request a new password reset.');
      return;
    }

    const setSessionAndUser = async () => {
      try {
        const { error } = await supabase.auth.setSession({
          access_token: hashAccessToken,
          refresh_token: hashRefreshToken,
        });

        if (error) {
          throw error;
        }

        setIsSessionSet(true);
      } catch (err: any) {
        console.error('Error setting session:', err);
        setError('Failed to verify reset link. Please request a new password reset.');
      }
    };

    setSessionAndUser();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      const { error } = await updatePassword(password);

      if (error) {
        throw error;
      }

      setSuccess(true);

      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (error: any) {
      setError(error.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-white w-full border-b border-black/10">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <img src="/logo.svg" alt="Logo" className="h-8" />
        </div>
      </div>

      <div className="bg-red-700 w-full">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <h1 className="text-white text-xl font-medium">
            {t('auth.resetYourPassword')}
          </h1>
          <div className="[&_button]:bg-transparent [&_button]:border-white/20 [&_button]:hover:border-white/40 [&_button]:text-white [&_svg]:text-white/60 [&_.absolute]:bg-white [&_.absolute]:border-black/15 [&_.absolute]:rounded-none [&_.absolute_button]:hover:bg-black/5 [&_.absolute_button]:text-black [&_.absolute_button.bg-slate-700\\/50]:bg-transparent [&_.absolute_button.bg-slate-700\\/50]:text-red-700 [&_.absolute_button.bg-slate-700\\/50]:border-l-2 [&_.absolute_button.bg-slate-700\\/50]:border-l-red-700 [&_.absolute_svg.text-blue-400]:text-red-700">
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      <div className="bg-white max-w-7xl mx-auto px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div className="max-w-md">
            {error && !success && (
              <div className="mb-6 text-sm text-red-700 border-l-2 border-red-700 pl-4 py-2">
                <div className="flex items-center gap-2">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            {success ? (
              <div className="py-6">
                <div className="w-16 h-16 border-2 border-green-600 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h3 className="text-2xl font-semibold text-black mb-3">{t('auth.passwordUpdated')}</h3>
                <p className="text-black/70 mb-6">
                  {t('auth.passwordUpdatedSuccessfully')} Logging you in...
                </p>
                <div className="w-5 h-5 border-2 border-black/10 border-t-red-700 rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <p className="text-black/70 mb-8">
                  {t('auth.enterYourNewPasswordBelow')}
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-xs uppercase tracking-wide text-black/70 mb-2 font-medium">{t('auth.newPassword')}</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black/30" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('auth.enterYourNewPassword')}
                        className="w-full bg-white text-black pl-10 pr-10 py-3 border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors placeholder:text-black/30"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black/30 hover:text-black transition-colors"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <p className="text-xs text-black/50 mt-1 ml-1">
                      {t('auth.mustBeAtLeast6Characters')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wide text-black/70 mb-2 font-medium">{t('auth.confirmNewPassword')}</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black/30" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder={t('auth.confirmYourNewPassword')}
                        className="w-full bg-white text-black pl-10 pr-10 py-3 border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors placeholder:text-black/30"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black/30 hover:text-black transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !isSessionSet || !password || !confirmPassword}
                    className="w-full bg-[#d91222] text-white hover:text-red-900 disabled:text-black/20 py-4 font-semibold transition-colors flex items-center justify-center gap-2 mt-2 border border-red-700 hover:border-red-900 disabled:border-black/10 rounded"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-black/10 border-t-red-700 rounded-full animate-spin" />
                    ) : (
                      t('profile.updatePassword')
                    )}
                  </button>

                  {error && (
                    <Link
                      to="/forgot-password"
                      className="w-full flex items-center justify-center gap-2 text-black/70 hover:text-red-700 transition-colors py-2"
                    >
                      {t('auth.resetPassword')}
                    </Link>
                  )}

                  <div className="mt-4 text-xs text-black/50">
                    {t('auth.secureConnection')}
                  </div>
                </form>
              </>
            )}
          </div>

          <div className="flex flex-col items-center justify-center text-center space-y-6">
            <img
              src="https://sunny-gaufre-664069.netlify.app/capital/en/1.png"
              alt="Security"
              className="w-full rounded-lg"
            />
            <p className="text-lg text-black/70 font-medium">
              Secure password recovery for your account
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
